import { acceptHMRUpdate, defineStore } from 'pinia';
import {
  type AddProductsOptions,
  type CartActionModel,
  type CartType,
  CartTypes,
  type LoadCartOptions,
  type ModifyCartItem,
  modifyCartItemSchema,
  type RemoveFromCartOptions,
  type UpdateCartItem,
  type UpdateProductsOptions
} from './model';
import type {
  AddProductsToCartMutationVariables,
  AdyenAdditionalData,
  AdyenPaymentDetailsMutation,
  AdyenSaveStateDataMutation,
  AvailableShippingMethodFragment,
  CartItemUpdateInput,
  CartUserErrorsFragment,
  CheckoutAgreement,
  CheckTaxExemptionQuery,
  CommonProductFragment,
  PlaceOrderMutation,
  RemoveTaxExemptionMutation,
  SetShippingAddressesOnCartMutation,
  TaxExemptionUploadResult,
  UpdateProductsInCartMutationVariables
} from '~~/lib/Shop/generated/schema';
import { useCartLink } from './useCartLink';
import { useConfirmationStore } from '~/stores/confirmationStore/confirmationStore';
import { extractError } from '~/utils/extractError';
import { useCartCookie } from '~/composables/useCartCookie';
import type { CartItemPutResponseModel } from '~~/server/api/magento/cart/item.put';
import type { AddNbqToCartResponseModel } from '~~/server/api/magento/cart/add-nbq.post';
import type { UpdateCartResponseModel } from '~~/server/api/magento/cart/index.put';
import type { DeleteCartItemResponseModel } from '~~/server/api/magento/cart/item.delete';
import type { GetCartResponseModel } from '~~/server/api/magento/cart/index.post';
import type { EnrichedCartFragment } from '~~/server/utils/enrichCartWithProductBreadcrumbs';
import { mapCartItemsForAnalytics } from '~/stores/cartStore/helper/mapCartItemsForAnalytics';
import { usePdpConfiguration } from '~/composables/usePdpConfiguration';
import { useTriggerAddToCartEvent } from '~/stores/cartStore/useTriggerAddToCartEvent';
import { v } from 'h3-valibot';
import { isDefined } from '~/utils/guards/isDefined';
import {
  filterAppliedTaxRows,
  sumAppliedTaxAmounts
} from '~/utils/shop/appliedTaxRows';
import { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import { createEcommerceTrackingData } from '~/stores/cartStore/helper/createEcommerceTrackingData';
import { resolveEcommerceSourceForCartItems } from '~/utils/productFinderAnalytics/resolveEcommerceSourceForCartItems';
import {
  BASLER_EXPRESS_DELIVERY_CARRIER_CODE,
  BASLER_PICKUP_CARRIER_CODE,
  BASLER_STANDARD_DELIVERY_CARRIER_CODE,
  G4_EVENT_NAME_ADD_SHIPPING_INFO,
  G4_EVENT_NAME_REMOVE_FROM_CART,
  G4_EVENT_NAME_UPDATE_CART
} from '~~/lib/constants';
import {
  CART_ITEMS_MAX_PAGE_SIZE,
  CART_ITEMS_PAGE_SIZE
} from '~/stores/cartStore/constants';

function createFakeFileLike({
  name,
  size,
  type
}: {
  name: string;
  size: number;
  type: string;
}) {
  const realFile = new File([''], name, { type });

  return new Proxy(realFile, {
    get(target, prop) {
      if (prop === 'size') {
        return size;
      }
      return Reflect.get(target, prop);
    }
  });
}

function extractUserErrors(
  userErrors: ReadonlyArray<CartUserErrorsFragment> | undefined | null
) {
  if (!userErrors) {
    return;
  }

  const errors = userErrors.map(
    (error: CartUserErrorsFragment) => error.message
  );

  return errors.length === 0 ? undefined : errors;
}

function retrieveProductSku(cartItem: ModifyCartItem): string {
  if ('sku' in cartItem) {
    return cartItem.sku;
  }

  return cartItem.decoratedProduct!.sku!;
}

/**
 * Provides a store for the shopping cart.
 * GUI Related operations should be moved to a separate store / component /
 * composable and depend on this store.
 */
export const useCartStore = defineStore('shop-cart', () => {
  const {
    $shopStore,
    $activeLocale,
    $globalPageSettings,
    $textDictionary,
    $analytics
  } = useNuxtApp();
  const slug = useSlug();
  const confirmationStore = useConfirmationStore();
  const { isAuthenticated, user } = useShopAuth();

  const currentAction = ref<CartActionModel | null>(null);
  const lastAction = ref<CartActionModel | null>(null);
  const loading = ref<boolean>(false);
  const itemsPageLoading = ref<boolean>(false);
  const currentPage = ref<Record<CartType, number>>({
    cart: 1,
    customerCart: 1,
    requestCart: 1
  });
  const cartLoadRequestId = ref<Record<CartType, number>>({
    cart: 0,
    customerCart: 0,
    requestCart: 0
  });
  const isMerging = ref<boolean>(false);
  const isInitializing = ref<boolean>(false);
  const initialized = ref<boolean>(false);
  const placedOrder = ref<PlaceOrderMutation | null>(null);
  const threeDSChallengeError = ref<string | null>(null);
  const adyenStateDataId = ref<Record<CartType, string | null>>({
    cart: null,
    requestCart: null,
    customerCart: null
  });
  const currentCouponCode = ref<string | null>(null);
  const pickupDeliveryId = ref<string | null>(null);
  const freightForwarder = ref<string | null>(null);
  const agreements = ref<CheckoutAgreement[]>([]);

  const carts = ref<Record<CartType, EnrichedCartFragment | null>>({
    cart: null,
    customerCart: null,
    requestCart: null
  });
  /** Page-1 snapshot for the mini-cart widget (independent of overview pagination). */
  const widgetCarts = ref<Record<CartType, EnrichedCartFragment | null>>({
    cart: null,
    customerCart: null,
    requestCart: null
  });
  /** Full-cart fast-delivery scan; null while pending (express hidden conservatively). */
  const allItemsSupportFastDelivery = ref<Record<CartType, boolean | null>>({
    cart: null,
    customerCart: null,
    requestCart: null
  });
  const temporaryAmountChangesPerCart = ref<Record<CartType, number>>({
    cart: 0,
    customerCart: 0,
    requestCart: 0
  });
  const taxExemptionFiles = ref<File[]>([]);
  const uploadedTaxExemptionFiles = ref<File[]>([]);
  const taxExemptSelection = ref<boolean | null>(null);

  interface TaxExemptionMessage {
    type: 'error' | 'success';
    text: string;
  }
  const taxExemptionMessages = ref<TaxExemptionMessage[]>([]);

  const activeCartType = ref<CartType>(
    isAuthenticated.value ? CartTypes.CUSTOMER_CART : CartTypes.CART
  );

  const relevantCarts = computed(() => {
    if (isAuthenticated.value) {
      return {
        customerCart: carts.value.customerCart,
        requestCart: carts.value.requestCart
      };
    }
    return {
      cart: carts.value.cart,
      requestCart: carts.value.requestCart
    };
  });

  const relevantCartTypes = computed(() => {
    if (isAuthenticated.value) {
      return [CartTypes.CUSTOMER_CART, CartTypes.REQUEST_CART];
    }
    return [CartTypes.CART, CartTypes.REQUEST_CART];
  });

  const _state = {
    currentAction,
    lastAction,
    loading,
    isMerging,
    carts,
    relevantCarts,
    activeCartType,
    adyenStateDataId
  };

  const activeCart = computed(() => {
    return carts.value[activeCartType.value];
  });

  const activeCurrentPage = computed(
    () => currentPage.value[activeCartType.value]
  );

  const totalPages = computed(
    () => activeCart.value?.itemsV2?.page_info?.total_pages ?? 1
  );

  const totalItemCount = computed(
    () => activeCart.value?.itemsV2?.total_count ?? 0
  );

  const pageSize = computed(
    () =>
      activeCart.value?.itemsV2?.page_info?.page_size ?? CART_ITEMS_PAGE_SIZE
  );

  const currentCartLink = computed<string | undefined>(() => {
    return useCartLink(activeCartType.value);
  });

  const availableCarts = computed<Record<string, string | undefined | null>>(
    () => {
      return Object.fromEntries(
        Object.entries(cartIds.value).map(([type, storeCartIdMap]) => [
          type,
          storeCartIdMap?.[$shopStore.value]
        ])
      );
    }
  );

  const cartIds = computed(() => {
    return {
      // could use ref directly - but looses actual function to sync cookie
      cart: useCartCookie(CartTypes.CART, $activeLocale.value.webapp).value,
      requestCart: useCartCookie(
        CartTypes.REQUEST_CART,
        $activeLocale.value.webapp
      ).value,
      customerCart: useCartCookie(
        CartTypes.CUSTOMER_CART,
        $activeLocale.value.webapp
      ).value
    };
  });

  const shopBaseUrlMap = computed<Record<string, string | undefined>>(() => {
    const pdpConfiguration = usePdpConfiguration();

    return {
      default: pdpConfiguration.value?.defaultConfiguration?.baseUrl,
      ...Object.fromEntries(
        Object.entries(pdpConfiguration.value?.configurationByLocale ?? {}).map(
          ([key, value]) => [key, value.baseUrl]
        )
      )
    };
  });

  const profileLink = computed<string>(() => {
    return `${
      shopBaseUrlMap.value[$activeLocale.value.webapp] ??
      shopBaseUrlMap.value.default
    }/${$shopStore.value}/customer/account/login/`;
  });

  const selectedPaymentMethod = computed(
    () => activeCart.value?.selected_payment_method?.code ?? null
  );

  const hasPayment = computed(() => {
    return activeCart.value?.selected_payment_method?.code !== '';
  });

  const totalExcludingTax = computed(() => {
    return activeCart.value?.prices?.subtotal_excluding_tax;
  });

  const appliedTaxes = computed(() =>
    filterAppliedTaxRows(activeCart.value?.prices?.applied_taxes)
  );

  const tax = computed(() => sumAppliedTaxAmounts(appliedTaxes.value));

  const hasTax = computed(() => appliedTaxes.value.length > 0);

  const total = computed(() => {
    return activeCart.value?.prices?.grand_total;
  });

  const numberOfArticles = computed(() => {
    return (
      (activeCart.value?.total_quantity ?? 0) +
      temporaryAmountChangesPerCart.value[activeCartType.value]
    );
  });

  const overAllNumberOfArticlesInRelevantCarts = computed(() => {
    return (
      Object.values(relevantCarts.value).reduce((acc, cur) => {
        acc += cur?.total_quantity ?? 0;
        return acc;
      }, 0) +
      relevantCartTypes.value.reduce((acc, cartType) => {
        acc += temporaryAmountChangesPerCart.value[cartType];
        return acc;
      }, 0)
    );
  });

  const isCustomerCartActive = computed(() => isAuthenticated.value);

  const numberOfArticlesCustomerCart = computed(() => {
    return (
      carts.value.customerCart?.total_quantity ??
      0 + temporaryAmountChangesPerCart.value[CartTypes.CUSTOMER_CART]
    );
  });

  const numberOfArticlesCart = computed(() => {
    return (
      carts.value.cart?.total_quantity ??
      0 + temporaryAmountChangesPerCart.value[CartTypes.CART]
    );
  });

  const numberOfArticlesRequestCart = computed(() => {
    return (
      carts.value.requestCart?.total_quantity ??
      0 + temporaryAmountChangesPerCart.value[CartTypes.REQUEST_CART]
    );
  });

  const hasTariff = computed(() => {
    return activeCart.value?.prices?.fooman_applied_surcharges?.length ?? false;
  });

  const tariff = computed(() => {
    return activeCart.value?.prices?.fooman_applied_surcharges?.[0]?.amount;
  });

  const activeCartItems = computed(() => {
    const itemBreadcrumbs = activeCart.value?.itemBreadcrumbs ?? {};

    return (
      activeCart.value?.itemsV2?.items.filter(isDefined).map((item) => {
        const product = item.product as unknown as CommonProductFragment;

        return {
          ...item,
          decoratedProduct: DecoratedProduct.build(
            product,
            $textDictionary.value,
            $activeLocale.value.webapp
          ),
          breadcrumbs: item.uid ? itemBreadcrumbs[item.uid] : undefined
        };
      }) ?? []
    );
  });

  const analyticsCartItems = computed(() =>
    mapCartItemsForAnalytics(activeCartItems.value)
  );

  // Prefer Magento's same_as_billing flag. Cart address `id` is the customer
  // address id and is null for inline/guest (NBQ) addresses — comparing ids
  // alone would treat null === null as "equals billing" and hide a different
  // shipping address in the checkout UI (DBP-4173).
  const billingIsShippingAddress = computed(() => {
    const shipping = shippingAddress.value;
    const billing = billingAddress.value;

    if (!isDefined(shipping) || !isDefined(billing)) {
      return false;
    }

    if ('same_as_billing' in shipping && shipping.same_as_billing === true) {
      return true;
    }

    if (
      'id' in shipping &&
      'id' in billing &&
      isDefined(shipping.id) &&
      isDefined(billing.id)
    ) {
      return shipping.id === billing.id;
    }

    return false;
  });

  const hasShippingAddress = computed(() => {
    return activeCart.value?.shipping_addresses?.length;
  });

  const shippingAddress = computed(
    () => activeCart.value?.shipping_addresses?.[0] ?? null
  );

  const hasBillingAddress = computed(() => {
    return activeCart.value?.billing_address;
  });

  const billingAddress = computed(() => {
    return activeCart.value?.billing_address;
  });

  const hasItemsOnlyWithStandardShipping = computed(() => {
    const cartType = activeCartType.value;
    const allSupport = allItemsSupportFastDelivery.value[cartType];
    if (allSupport === null) {
      // A cart loaded outside checkout never starts the async scan, so null
      // must not hide Express when every line item is already in memory.
      const cart = carts.value[cartType];
      const items = cart?.itemsV2?.items?.filter(isDefined) ?? [];
      const total = cart?.itemsV2?.total_count ?? 0;
      if (total > 0 && items.length >= total) {
        return !items.every(cartItemSupportsFastDelivery);
      }

      return true;
    }

    return !allSupport;
  });

  const shouldShowPricesInRequestCart = computed(() => {
    // REQUEST_CART: hide prices if not all pages are loaded or a visible item
    // has showPrice = false (conservative when paginated — see design.md).
    if (activeCartType.value !== CartTypes.REQUEST_CART) {
      return true;
    }

    const loadedItemCount = activeCartItems.value.length;
    const totalCount =
      activeCart.value?.itemsV2?.total_count ?? loadedItemCount;

    if (totalCount > loadedItemCount) {
      return false;
    }

    return activeCartItems.value.every(
      (item) => item.decoratedProduct.showPrice
    );
  });

  const availablePaymentMethods = computed(() => {
    return (
      activeCart.value?.available_payment_methods
        ?.filter(isDefined)
        ?.map((pm) => pm.code) ?? []
    );
  });

  const availableShippingMethods = computed(() => {
    const shipMethods = shippingAddress.value?.available_shipping_methods ?? [];

    if (hasItemsOnlyWithStandardShipping.value) {
      return shipMethods
        ?.filter(isDefined)
        ?.filter((i) => i.carrier_code !== BASLER_EXPRESS_DELIVERY_CARRIER_CODE)
        ?.filter(isDefined);
    }

    return shipMethods?.filter(isDefined);
  });

  const isPickupSelected = computed(
    () =>
      selectedShippingMethod.value?.carrier_code === BASLER_PICKUP_CARRIER_CODE
  );

  const selectedShippingMethod = computed(
    () => shippingAddress.value?.selected_shipping_method ?? null
  );

  const appliedCouponCodes = computed(
    () => activeCart.value?.applied_coupons?.filter(isDefined) ?? []
  );

  const appliedDiscounts = computed(
    () => activeCart.value?.prices?.discounts?.filter(isDefined) ?? []
  );

  const handlePickupDeliveryIdUpdate = () => {
    if (
      Array.isArray(activeCart.value?.shipping_addresses) &&
      isDefined(activeCart.value?.shipping_addresses?.[0]) &&
      typeof activeCart.value?.shipping_addresses?.[0] === 'object' &&
      'delivery_id' in (activeCart.value?.shipping_addresses?.[0] ?? {})
    ) {
      const deliveryId = activeCart.value?.shipping_addresses?.[0]?.delivery_id;

      pickupDeliveryId.value = isDefined(deliveryId) ? deliveryId : null;
    }
  };

  const handleFreightForwarderUpdate = () => {
    if (
      Array.isArray(activeCart.value?.shipping_addresses) &&
      isDefined(activeCart.value?.shipping_addresses?.[0]) &&
      typeof activeCart.value?.shipping_addresses?.[0] === 'object' &&
      'freight_forwarder' in (activeCart.value?.shipping_addresses?.[0] ?? {})
    ) {
      const ff = activeCart.value?.shipping_addresses?.[0]?.freight_forwarder;

      freightForwarder.value = isDefined(ff) ? ff : null;
    }
  };

  async function fetchCartFromApi(
    type: CartType,
    pagination: { currentPage: number; pageSize: number },
    cartId?: string
  ) {
    return $fetch<GetCartResponseModel>('/api/magento/cart', {
      method: 'POST',
      query: {
        type,
        locale: $activeLocale.value.webapp,
        store: $shopStore.value,
        currentPage: pagination.currentPage,
        pageSize: pagination.pageSize,
        ...(cartId ? { cartId } : {})
      }
    });
  }

  async function fetchCartProductsPageFromApi(
    type: CartType,
    pagination: { currentPage: number; pageSize: number },
    cartId?: string
  ) {
    return $fetch<GetCartResponseModel>('/api/magento/cart/products/', {
      method: 'GET',
      query: {
        type,
        locale: $activeLocale.value.webapp,
        store: $shopStore.value,
        currentPage: pagination.currentPage,
        pageSize: pagination.pageSize,
        ...(cartId ? { cartId } : {})
      }
    });
  }

  function partiallyUpdateActiveCart(
    partiallyUpdate?: Partial<EnrichedCartFragment> | null
  ) {
    if (partiallyUpdate) {
      Object.keys(partiallyUpdate).forEach((key) => {
        if (!carts.value[activeCartType.value]) {
          carts.value[activeCartType.value] = {} as EnrichedCartFragment;
        }
        // @ts-ignore
        carts.value[activeCartType.value][key] = partiallyUpdate[key];
      });
    }
  }

  function cartItemSupportsFastDelivery(
    item: NonNullable<
      NonNullable<NonNullable<EnrichedCartFragment['itemsV2']>['items']>[number]
    >
  ): boolean {
    const product = item.product as unknown as CommonProductFragment;

    return DecoratedProduct.build(
      product,
      $textDictionary.value,
      $activeLocale.value.webapp
    ).hasFastDelivery;
  }

  function isCheckoutRoute(): boolean {
    const checkoutSlugs = [
      $globalPageSettings.value?.shopConfig?.cartCheckoutPage?.slug,
      $globalPageSettings.value?.shopConfig?.requestCartCheckoutPage?.slug
    ].filter(isDefined);

    return checkoutSlugs.includes(slug.value);
  }

  async function syncWidgetCartSnapshot(cartType: CartType) {
    if (currentPage.value[cartType] === 1 && carts.value[cartType]) {
      widgetCarts.value[cartType] = carts.value[cartType];
      return;
    }

    try {
      widgetCarts.value[cartType] = await fetchCartFromApi(cartType, {
        currentPage: 1,
        pageSize: CART_ITEMS_PAGE_SIZE
      });
    } catch (error) {
      useLogger().warn(
        'cartStore: failed to refresh mini-cart widget snapshot for %s — keeping previous snapshot',
        cartType,
        error
      );
    }
  }

  async function syncCartFastDeliveryEligibility(cartType: CartType) {
    const totalCount = carts.value[cartType]?.itemsV2?.total_count ?? 0;

    if (totalCount === 0) {
      allItemsSupportFastDelivery.value[cartType] = true;
      return;
    }

    const currentCart = carts.value[cartType];
    const loadedItems = currentCart?.itemsV2?.items?.filter(isDefined) ?? [];

    if (loadedItems.length >= totalCount) {
      allItemsSupportFastDelivery.value[cartType] = loadedItems.every(
        cartItemSupportsFastDelivery
      );
      return;
    }

    try {
      let page = 1;
      let remaining = totalCount;

      while (remaining > 0) {
        const cartPage = await fetchCartProductsPageFromApi(cartType, {
          currentPage: page,
          pageSize: CART_ITEMS_MAX_PAGE_SIZE
        });
        const items = cartPage?.itemsV2?.items?.filter(isDefined) ?? [];

        if (!items.every(cartItemSupportsFastDelivery)) {
          allItemsSupportFastDelivery.value[cartType] = false;
          return;
        }

        remaining -= items.length;
        if (items.length === 0) {
          break;
        }
        page++;
      }

      allItemsSupportFastDelivery.value[cartType] = true;
    } catch (error) {
      allItemsSupportFastDelivery.value[cartType] = null;
      useLogger().warn(
        'cartStore: failed to scan cart fast-delivery eligibility for %s — express shipping hidden until retry',
        cartType,
        error
      );
    }
  }

  interface CartPageSyncOptions {
    /** Re-run full-cart fast-delivery scan (item add/update/remove only). */
    rescanFastDelivery?: boolean;
    /**
     * Known Magento cart id from a preceding mutation (e.g. addProductsToCart).
     * Passed through so the refresh uses the same quote even if pdpCart is stale.
     */
    cartId?: string;
  }

  async function syncCartAuxiliaryData(
    cartType: CartType,
    options?: CartPageSyncOptions
  ) {
    await syncWidgetCartSnapshot(cartType);

    if (!isCheckoutRoute()) {
      return;
    }

    if (
      options?.rescanFastDelivery === true ||
      allItemsSupportFastDelivery.value[cartType] === null
    ) {
      await syncCartFastDeliveryEligibility(cartType);
    }
  }

  // The cart is often loaded on the PDP. Entering checkout must still scan
  // carts whose items are not all in memory yet.
  watch(
    () =>
      isCheckoutRoute()
        ? (carts.value[activeCartType.value]?.itemsV2?.total_count ?? 0)
        : 0,
    () => {
      if (!isCheckoutRoute()) {
        return;
      }

      const cartType = activeCartType.value;
      if (allItemsSupportFastDelivery.value[cartType] !== null) {
        return;
      }

      const total = carts.value[cartType]?.itemsV2?.total_count ?? 0;
      if (total === 0) {
        return;
      }

      void syncCartFastDeliveryEligibility(cartType);
    }
  );

  function isLatestCartLoadRequest(
    cartType: CartType,
    requestId: number
  ): boolean {
    return requestId === cartLoadRequestId.value[cartType];
  }

  /**
   * Fetches a cart page, clamps to the last valid page when needed, and applies
   * the result to store state. Returns null when a newer load superseded this
   * request (stale response).
   */
  async function fetchAndApplyCartPage(
    cartType: CartType,
    page: number,
    pageSize: number,
    requestId?: number,
    cartId?: string
  ): Promise<number | null> {
    const isStale = () =>
      requestId !== undefined && !isLatestCartLoadRequest(cartType, requestId);

    let resolvedPage = page;
    let cart = await fetchCartFromApi(
      cartType,
      {
        currentPage: resolvedPage,
        pageSize
      },
      cartId
    );

    if (isStale()) {
      return null;
    }

    const totalPagesForCart = cart?.itemsV2?.page_info?.total_pages ?? 1;

    if (resolvedPage > totalPagesForCart) {
      resolvedPage = Math.max(1, totalPagesForCart);
      cart = await fetchCartFromApi(
        cartType,
        {
          currentPage: resolvedPage,
          pageSize
        },
        cartId
      );

      if (isStale()) {
        return null;
      }
    }

    carts.value[cartType] = cart;
    if (resolvedPage === 1) {
      widgetCarts.value[cartType] = cart;
    }
    handlePickupDeliveryIdUpdate();
    handleFreightForwarderUpdate();

    return resolvedPage;
  }

  async function syncCartPageAfterMutation(
    cartType: CartType,
    options?: CartPageSyncOptions
  ) {
    const page = currentPage.value[cartType];
    if (isCheckoutRoute() && options?.rescanFastDelivery) {
      allItemsSupportFastDelivery.value[cartType] = null;
    }

    const resolvedPage = await fetchAndApplyCartPage(
      cartType,
      page,
      CART_ITEMS_PAGE_SIZE,
      undefined,
      options?.cartId
    );

    if (resolvedPage !== null) {
      currentPage.value[cartType] = resolvedPage;
    }

    syncCartAuxiliaryData(cartType, options);
  }

  /**
   * Adding a product which is already in the cart will increase the
   * amount accordingly.
   * Performs internal error handling.
   *
   * @param cartItems The items to add.
   * @param cartType The cart type to use.
   * @param options Additional options to use.
   */
  async function addProducts(
    cartItems: ReadonlyArray<ModifyCartItem>,
    cartType: CartType,
    options?: AddProductsOptions
  ) {
    const logger = useLogger();
    if (!cartItems.length) {
      logger.warn('cartStore: no elements defined to add to cart - aborting.');

      return;
    }

    const parsedItems = cartItems.map((item) =>
      v.parse(modifyCartItemSchema, item)
    );

    const action: CartActionModel = {
      type: 'addItems',
      productName: options?.productName
    };
    currentAction.value = action;

    loading.value = true;

    try {
      // the last modified cart should be active
      // @see https://gcp.baslerweb.com/jira/browse/WEB2-1577
      setActiveCartType(cartType);
      const addItemResponse = await $fetch<CartItemPutResponseModel>(
        '/api/magento/cart/item',
        {
          method: 'PUT',
          query: {
            store: $shopStore.value,
            locale: $activeLocale.value.webapp,
            type: cartType
          },
          body: {
            cartItems: parsedItems.map((cartItem) => ({
              sku: retrieveProductSku(cartItem),
              quantity: cartItem.quantity,
              config_id: cartItem?.config_id ?? ''
            }))
          } satisfies Pick<AddProductsToCartMutationVariables, 'cartItems'>
        }
      );
      if (addItemResponse?.cart) {
        const cartId = addItemResponse.cart.id;
        // Keep pdpCart in sync so later requests (reload, Magento handoff) reuse
        // the same quote — not only the immediate post-add refresh.
        const cartCookie = useCartCookie(cartType, $activeLocale.value.webapp);
        cartCookie.value = {
          ...cartCookie.value,
          [$shopStore.value]: cartId
        };

        await syncCartPageAfterMutation(cartType, {
          rescanFastDelivery: true,
          cartId
        });
        // XXX: proper typing
        action.error = extractUserErrors(addItemResponse.user_errors as never);
        action.success = true;
      }

      useTriggerAddToCartEvent(
        parsedItems,
        cartType,
        carts.value[cartType]?.id ?? addItemResponse?.cart?.id ?? '',
        $shopStore.value,
        $activeLocale.value.webapp,
        options
      );
    } catch (e) {
      action.error = extractError(e);
      action.success = false;
    } finally {
      loading.value = false;
      lastAction.value = action;
      currentAction.value = null;
    }
  }

  /**
   * Adds all items from an NBQ quote into the customer cart and stores the
   * NBQ number on that cart for the eventual sales order.
   */
  async function addNbqToCart(quoteUid: string) {
    const action: CartActionModel = {
      type: 'addItems',
      // Avoid the generic "product {productName} added" toast — NBQ is a bulk add.
      silent: true
    };
    currentAction.value = action;
    loading.value = true;

    try {
      setActiveCartType(CartTypes.CUSTOMER_CART);
      const response = await $fetch<AddNbqToCartResponseModel>(
        '/api/magento/cart/add-nbq',
        {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $activeLocale.value.webapp,
            type: CartTypes.CUSTOMER_CART
          },
          body: {
            quoteUid
          }
        }
      );

      if (!response?.status || !response.cart?.id) {
        action.error = response?.message
          ? [response.message]
          : ['Failed to add NBQ items to cart'];
        action.success = false;
        return;
      }

      const cartId = response.cart.id;
      const cartCookie = useCartCookie(
        CartTypes.CUSTOMER_CART,
        $activeLocale.value.webapp
      );
      cartCookie.value = {
        ...cartCookie.value,
        [$shopStore.value]: cartId
      };

      await syncCartPageAfterMutation(CartTypes.CUSTOMER_CART, {
        rescanFastDelivery: true,
        cartId
      });
      action.success = true;
    } catch (e) {
      action.error = extractError(e);
      action.success = false;
    } finally {
      loading.value = false;
      lastAction.value = action;
      currentAction.value = null;
    }
  }

  async function updateProducts(
    updateCartItems: ReadonlyArray<UpdateCartItem>,
    cartType: CartType,
    options?: UpdateProductsOptions
  ) {
    const logger = useLogger();
    if (!updateCartItems.length) {
      logger.warn(
        'cartStore: no elements defined to update in cart - aborting.'
      );

      return;
    }
    setActiveCartType(cartType);

    const action: CartActionModel = {
      type: 'updateItems',
      productName: options?.productName
    };
    currentAction.value = action;

    loading.value = true;

    const updatedCartItems = updateCartItems
      .filter((updateCartItem) => {
        const cartItem = activeCartItems.value.find(
          (innerItem) => updateCartItem.uid === innerItem.uid
        );

        return !cartItem || updateCartItem.quantity !== cartItem.quantity;
      })
      .map((UpdateCartItem) => {
        const cartItem = activeCartItems.value.find(
          (innerItem) => UpdateCartItem.uid === innerItem.uid
        );

        if (!cartItem) {
          temporaryAmountChangesPerCart.value[activeCartType.value] +=
            UpdateCartItem.quantity;
        } else {
          if (UpdateCartItem.quantity < cartItem.quantity) {
            temporaryAmountChangesPerCart.value[activeCartType.value] +=
              (cartItem.quantity - UpdateCartItem.quantity) * -1;
          } else {
            temporaryAmountChangesPerCart.value[activeCartType.value] +=
              UpdateCartItem.quantity - cartItem.quantity;
          }
        }

        if (cartItem && cartItem.decoratedProduct) {
          return {
            quantity: UpdateCartItem.quantity,
            decoratedProduct: cartItem.decoratedProduct,
            breadcrumbs: cartItem.breadcrumbs
          };
        }
      })
      .filter(isDefined);

    try {
      // the last modified cart should be active
      // @see https://gcp.baslerweb.com/jira/browse/WEB2-1577
      // TODO
      await $fetch<UpdateCartResponseModel>('/api/magento/cart', {
        method: 'PUT',
        query: {
          store: $shopStore.value,
          locale: $activeLocale.value.webapp,
          type: activeCartType.value
        },
        body: {
          cartItems: updateCartItems.map(
            (cartItem) =>
              ({
                cart_item_uid: cartItem.uid,
                quantity: cartItem.quantity
              }) satisfies CartItemUpdateInput
          )
        } satisfies Pick<UpdateProductsInCartMutationVariables, 'cartItems'>
      });
      await syncCartPageAfterMutation(activeCartType.value, {
        rescanFastDelivery: true
      });

      $analytics.pushToDataLayer({
        event: G4_EVENT_NAME_UPDATE_CART,
        userId: user?.value?.claims?.oid?.toString() ?? 'null',
        ecommerce: await createEcommerceTrackingData({
          cartItems: updatedCartItems,
          cartId: activeCart.value?.id ?? undefined,
          source: resolveEcommerceSourceForCartItems(
            updatedCartItems,
            options?.source
          )
        })
      });
    } catch (e) {
      action.error = extractError(e);
    } finally {
      loading.value = false;
      lastAction.value = action;
      currentAction.value = null;
      temporaryAmountChangesPerCart.value[activeCartType.value] = 0;
    }
  }

  /**
   * Removes the given product from the given cart type.
   * If no cart type is given, the current active cart is used.
   * Performs internal error handling.
   *
   * @param productUid The product uid to remove.
   * @param cartType The cart type to use.
   */

  async function removeFromCart(
    productUid: string,
    cartType?: CartType,
    options?: RemoveFromCartOptions
  ) {
    setActiveCartType(cartType ?? activeCartType.value);

    const action: CartActionModel = {
      type: 'removeItem',
      silent: options?.silent
    };
    currentAction.value = action;
    loading.value = true;

    const removedCartItems = [];
    const cartItem = activeCartItems.value.find(
      (innerItem) => productUid === innerItem.uid
    );

    if (cartItem?.decoratedProduct) {
      temporaryAmountChangesPerCart.value[activeCartType.value] =
        cartItem.quantity * -1;
      removedCartItems.push({
        quantity: 0,
        decoratedProduct: cartItem?.decoratedProduct,
        breadcrumbs: cartItem?.breadcrumbs
      });
    }

    try {
      // the last modified cart should be active
      // @see https://gcp.baslerweb.com/jira/browse/WEB2-1577
      //TODO
      await $fetch<DeleteCartItemResponseModel>('/api/magento/cart/item', {
        method: 'DELETE',
        query: {
          store: $shopStore.value,
          locale: $activeLocale.value.webapp,
          type: activeCartType.value,
          cartItemUid: productUid
        }
      });
      await syncCartPageAfterMutation(activeCartType.value, {
        rescanFastDelivery: true
      });

      $analytics.pushToDataLayer({
        event: G4_EVENT_NAME_REMOVE_FROM_CART,
        userId: user?.value?.claims?.oid?.toString() ?? 'null',
        ecommerce: await createEcommerceTrackingData({
          cartItems: removedCartItems,
          cartId: activeCart.value?.id ?? undefined,
          source: resolveEcommerceSourceForCartItems(removedCartItems)
        })
      });
    } catch (e) {
      action.error = extractError(e);
    } finally {
      loading.value = false;
      lastAction.value = action;
      currentAction.value = null;
      temporaryAmountChangesPerCart.value[activeCartType.value] = 0;
    }
  }

  /**
   * (Re)loads the given cart type.
   * This ensures, that a cart actually exists (if not, a new one
   * is created).
   * If no cart type is given, the current active cart is used.
   * Performs internal error handling.
   *
   * @param type The cart type to load.
   * @param options
   */
  async function loadCart(type?: CartType, options?: LoadCartOptions) {
    if (!isAuthenticated.value && type === CartTypes.CUSTOMER_CART) {
      return;
    }
    if (isAuthenticated.value && type === CartTypes.CART) {
      return;
    }

    const cartType = type ?? activeCartType.value;
    setActiveCartType(cartType);

    const page = options?.currentPage ?? currentPage.value[cartType];
    const pageSizeOption = options?.pageSize ?? CART_ITEMS_PAGE_SIZE;
    cartLoadRequestId.value[cartType]++;
    const requestId = cartLoadRequestId.value[cartType];

    const action: CartActionModel = {
      type: 'loadCart'
    };
    currentAction.value = action;

    const useItemsPageLoading = options?.itemsPageLoading === true;

    if (useItemsPageLoading) {
      itemsPageLoading.value = true;
    } else {
      loading.value = true;
    }

    if (isCheckoutRoute() && !useItemsPageLoading) {
      allItemsSupportFastDelivery.value[cartType] = null;
    }

    try {
      const resolvedPage = await fetchAndApplyCartPage(
        cartType,
        page,
        pageSizeOption,
        requestId
      );

      if (resolvedPage !== null) {
        currentPage.value[cartType] = resolvedPage;
        if (!useItemsPageLoading) {
          syncCartAuxiliaryData(cartType);
        }
      }
    } catch (e) {
      if (isLatestCartLoadRequest(cartType, requestId)) {
        action.error = extractError(e);
      }
    } finally {
      if (isLatestCartLoadRequest(cartType, requestId)) {
        lastAction.value = action;
        currentAction.value = null;
        if (useItemsPageLoading) {
          itemsPageLoading.value = false;
        } else {
          loading.value = false;
        }
      }
    }
  }

  async function changeCartPage(page: number, type?: CartType) {
    const cartType = type ?? activeCartType.value;
    const totalPagesForCart =
      carts.value[cartType]?.itemsV2?.page_info?.total_pages ?? 1;

    if (
      page < 1 ||
      page > totalPagesForCart ||
      page === currentPage.value[cartType]
    ) {
      return;
    }

    await loadCart(cartType, { currentPage: page, itemsPageLoading: true });
  }

  /**
   * Loads all available carts ({@link this.availableCarts}).
   * Performs a request per available {@link CartType}.
   */
  let initPromise: Promise<void> | null = null;

  async function init(once = false) {
    if (isInitializing.value) {
      // Await the in-flight init rather than busy-polling.
      await (initPromise ?? Promise.resolve());
      return;
    }

    // Wait for merge to complete before initializing
    // This prevents loading an empty customer cart while merge is in progress
    if (isMerging.value) {
      const maxWait = 10000; // 10 seconds timeout
      const startTime = Date.now();
      while (isMerging.value && Date.now() - startTime < maxWait) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    isInitializing.value = true;
    initPromise = (async (): Promise<void> => {
      if (!once || !initialized.value) {
        await Promise.all(
          Object.entries(availableCarts.value).map(async ([type, cartId]) => {
            if (cartId) {
              await loadCart(type as CartType);
            }
          })
        );
      }

      // display the cart which has actually items.
      // if no cart has "isActive" set - the empty dialog is displayed.
      const firstCartTypeWithItems = Object.entries(carts.value).find(
        ([_, cart]) => (cart?.itemsV2?.total_count ?? 0) > 0
      )?.[0] as CartType | undefined;
      if (firstCartTypeWithItems) {
        setActiveCartType(firstCartTypeWithItems);
      }

      if (
        [
          $globalPageSettings.value?.shopConfig?.cartPage?.slug,
          $globalPageSettings.value?.shopConfig?.cartCheckoutPage?.slug
        ].includes(slug.value)
      ) {
        setActiveCartType(CartTypes.CART);
      }
      if (
        [
          $globalPageSettings.value?.shopConfig?.requestCartPage?.slug,
          $globalPageSettings.value?.shopConfig?.requestCartCheckoutPage?.slug
        ].includes(slug.value)
      ) {
        setActiveCartType(CartTypes.REQUEST_CART);
      }

      initialized.value = true;
    })().finally(() => {
      isInitializing.value = false;
      initPromise = null;
    });

    await initPromise;
  }

  /**
   * FOR CHECKOUT
   */
  async function initForCheckout() {
    await init(true);
    await checkTaxExemption();
    await loadAgreements();
  }

  async function loadAgreements() {
    if (!isAuthenticated.value) {
      return;
    }

    const action: CartActionModel = {
      type: 'loadAgreements'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      agreements.value = await $fetch<CheckoutAgreement[]>(
        '/api/magento/checkout/agreements',
        {
          method: 'GET',
          query: {
            store: $shopStore.value,
            locale: $activeLocale.value.webapp
          }
        }
      );
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  }

  const addShippingAddress = async function (addressId: number) {
    const action: CartActionModel = {
      type: 'addShippingAddress'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      // the last modified cart should be active
      // @see https://gcp.baslerweb.com/jira/browse/WEB2-1577
      const updatedPartialCart = await $fetch<
        NonNullable<
          SetShippingAddressesOnCartMutation['setShippingAddressesOnCart']
        >['cart']
      >('/api/magento/checkout/shipping', {
        method: 'POST',
        query: {
          store: $shopStore.value,
          locale: $activeLocale.value.webapp,
          type: activeCartType.value,
          addressId,
          pickupDeliveryId:
            isDefined(pickupDeliveryId.value) && pickupDeliveryId.value.length
              ? pickupDeliveryId.value
              : undefined,
          freightForwarder:
            isDefined(freightForwarder.value) && freightForwarder.value.length
              ? freightForwarder.value
              : undefined
        }
      });
      partiallyUpdateActiveCart(updatedPartialCart);
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const addBillingAddress = async function (addressId: number) {
    const action: CartActionModel = {
      type: 'addBillingAddress'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      // the last modified cart should be active
      // @see https://gcp.baslerweb.com/jira/browse/WEB2-1577
      const updatedPartialCart = await $fetch('/api/magento/checkout/billing', {
        method: 'POST',
        query: {
          store: $shopStore.value,
          locale: $activeLocale.value.webapp,
          type: activeCartType.value,
          addressId
        }
      });
      partiallyUpdateActiveCart(updatedPartialCart);
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const addDeliveryMethod = async function (
    carrierCode: string,
    methodCode: string
  ) {
    const action: CartActionModel = {
      type: 'addDeliveryMethod'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      // the last modified cart should be active
      // @see https://gcp.baslerweb.com/jira/browse/WEB2-1577
      const partialCartUpdate = await $fetch<Partial<EnrichedCartFragment>>(
        '/api/magento/checkout/shippingmethod',
        {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $activeLocale.value.webapp,
            type: activeCartType.value,
            carrierCode,
            methodCode
          }
        }
      );

      partiallyUpdateActiveCart(partialCartUpdate);

      $analytics.pushToDataLayer({
        event: G4_EVENT_NAME_ADD_SHIPPING_INFO,
        userId: user?.value?.claims?.oid?.toString() ?? 'null',
        ecommerce: {
          ...(await createEcommerceTrackingData({
            cartItems: analyticsCartItems.value,
            cartId: activeCart.value?.id ?? undefined
          })),
          currency:
            activeCart.value?.prices?.subtotal_excluding_tax?.currency ??
            activeCart.value?.prices?.grand_total?.currency,
          value:
            activeCart.value?.prices?.subtotal_excluding_tax?.value ??
            activeCart.value?.prices?.grand_total?.value,
          shipping_tier:
            {
              [BASLER_EXPRESS_DELIVERY_CARRIER_CODE]: 'Express',
              [BASLER_PICKUP_CARRIER_CODE]: 'Pickup',
              [BASLER_STANDARD_DELIVERY_CARRIER_CODE]: 'Standard'
            }[carrierCode] ?? 'Standard'
        }
      });
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const setPaymentMethod = async function (
    paymentCode: string,
    adyenAdditionalData?: AdyenAdditionalData | null
  ) {
    if (!isAuthenticated.value) {
      return;
    }

    const action: CartActionModel = {
      type: 'setPaymentMethod'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      const partialCartUpdate = await $fetch<Partial<EnrichedCartFragment>>(
        '/api/magento/checkout/payment',
        {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $activeLocale.value.webapp,
            type: activeCartType.value,
            paymentCode
          },
          body:
            adyenAdditionalData != null ? { adyenAdditionalData } : undefined
        }
      );

      partiallyUpdateActiveCart(partialCartUpdate);
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const setAdyenStateData = async function (stateData: string) {
    const action: CartActionModel = {
      type: 'setAdyenStateData'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      adyenStateDataId.value[activeCartType.value] =
        (
          await $fetch<AdyenSaveStateDataMutation['adyenSaveStateData'] | null>(
            '/api/magento/checkout/payment/stateData',
            {
              method: 'POST',
              query: {
                store: $shopStore.value,
                locale: $activeLocale.value.webapp,
                type: activeCartType.value
              },
              body: {
                stateData
              }
            }
          )
        )?.stateDataId ?? null;
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const addAdyenPaymentDetails = async function (stateData: string) {
    const action: CartActionModel = {
      type: 'addAdyenPaymentDetails'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      return (
        (await $fetch<
          AdyenPaymentDetailsMutation['adyenPaymentDetails'] | null
        >('/api/magento/checkout/payment/details', {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $activeLocale.value.webapp,
            type: activeCartType.value
          },
          body: {
            stateData
          }
        })) ?? null
      );
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const placeOrder = async function () {
    const action: CartActionModel = {
      type: 'placeOrder'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      const response = await $fetch<PlaceOrderMutation>(
        '/api/magento/checkout/order',
        {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $activeLocale.value.webapp,
            type: activeCartType.value
          }
        }
      );

      if (
        isDefined(response.placeOrder) &&
        'errors' in response.placeOrder &&
        Array.isArray(response.placeOrder.errors) &&
        response.placeOrder.errors.length
      ) {
        action.error = response.placeOrder.errors[0]?.message;
      } else {
        placedOrder.value = response;

        confirmationStore.initByOrder(
          placedOrder.value.placeOrder?.order?.order_number ?? '',
          activeCart.value
        );
      }
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const commitPickupDeliveryId = async function () {
    if (
      isDefined(shippingAddress.value?.id) &&
      pickupDeliveryId.value &&
      pickupDeliveryId.value.length > 0
    ) {
      // addShippingAddress handles internal state of pickupLocationCode
      await addShippingAddress(shippingAddress.value.id!);
    }
  };

  const commitFreightForwarder = async function () {
    if (!isDefined(shippingAddress.value?.id)) {
      return;
    }

    const currentFreightForwarder =
      (shippingAddress.value as { freight_forwarder?: string | null })
        ?.freight_forwarder ?? null;

    // Only commit when the freight forwarder value has actually changed
    if (freightForwarder.value === currentFreightForwarder) {
      return;
    }

    await addShippingAddress(shippingAddress.value.id!);
  };

  const isSelectedShippingMethod = function (
    shippingMethod: AvailableShippingMethodFragment
  ): boolean {
    if (!selectedShippingMethod.value) {
      return false;
    }
    return (
      selectedShippingMethod.value.method_code == shippingMethod.method_code &&
      selectedShippingMethod.value.carrier_code == shippingMethod.carrier_code
    );
  };

  const applyCouponCode = async function (coupon?: string) {
    const couponCode = coupon ?? currentCouponCode.value;
    const action: CartActionModel = {
      type: 'addCoupon'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      const updatedPartialCart = await $fetch<Partial<EnrichedCartFragment>>(
        '/api/magento/checkout/coupon',
        {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $activeLocale.value.webapp,
            type: activeCartType.value,
            couponCode
          }
        }
      );

      partiallyUpdateActiveCart(updatedPartialCart);
      currentCouponCode.value = null;
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const removeCouponCode = async function (coupon: string) {
    const action: CartActionModel = {
      type: 'removeCoupon'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      const updatedPartialCart = await $fetch<Partial<EnrichedCartFragment>>(
        '/api/magento/checkout/coupon',
        {
          method: 'DELETE',
          query: {
            store: $shopStore.value,
            locale: $activeLocale.value.webapp,
            type: activeCartType.value,
            couponCode: coupon
          }
        }
      );

      partiallyUpdateActiveCart(updatedPartialCart);
      currentCouponCode.value = null;
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const checkTaxExemption = async function () {
    if (!isAuthenticated.value) {
      uploadedTaxExemptionFiles.value = [];
      return;
    }

    const action: CartActionModel = {
      type: 'checkTaxExemption'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      // Reset any previous client-side cache
      uploadedTaxExemptionFiles.value = [];

      const response = await $fetch<
        CheckTaxExemptionQuery['checkTaxExemption']
      >('/api/magento/checkout/taxexemption', {
        method: 'GET',
        query: {
          store: $shopStore.value,
          locale: $activeLocale.value.webapp,
          type: activeCartType.value
        }
      });

      if (response && response.success) {
        uploadedTaxExemptionFiles.value = [
          createFakeFileLike({
            name: (() => {
              if (response.name) {
                const strings = response.name.split('/');
                return strings[strings.length - 1];
              }
              return 'Tax Excemption Certificate';
            })(),
            size: response.size ?? 0,
            type: response.type ?? 'unknown'
          })
        ].filter(isDefined);
      }

      const taxExemptValue = (
        response as CheckTaxExemptionQuery['checkTaxExemption'] & {
          tax_exempt?: boolean | null;
        }
      )?.tax_exempt;
      if (taxExemptValue === true || taxExemptValue === false) {
        taxExemptSelection.value = taxExemptValue;
      }
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const setTaxExemptionChoice = async function (isTaxExempt: boolean) {
    const action: CartActionModel = {
      type: 'setTaxExemptionChoice'
    };
    currentAction.value = action;
    loading.value = true;

    try {
      const response = await $fetch<{
        success: boolean;
        tax_exempt: boolean | null;
      }>('/api/magento/checkout/taxexemption/status', {
        method: 'POST',
        query: {
          store: $shopStore.value,
          locale: $activeLocale.value.webapp
        },
        body: {
          isTaxExempt
        }
      });

      if (!response?.success) {
        throw new Error('Could not save tax exemption selection');
      }

      taxExemptSelection.value =
        response.tax_exempt === true || response.tax_exempt === false
          ? response.tax_exempt
          : null;
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const uploadTaxExemption = async function (file: File) {
    const action: CartActionModel = {
      type: 'uploadTaxExemption'
    };
    currentAction.value = action;
    loading.value = true;

    try {
      const formData = new FormData();
      formData.append('files', file);
      const response = await $fetch<TaxExemptionUploadResult>(
        '/api/magento/checkout/taxexemption',
        {
          method: 'POST',
          query: {
            store: $shopStore.value,
            locale: $activeLocale.value.webapp
          },
          body: formData
        }
      );

      if (response?.success) {
        taxExemptionFiles.value = [];
        uploadedTaxExemptionFiles.value = [file];
        taxExemptSelection.value = true;
        await checkTaxExemption();
      }
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const removeTaxExemption = async function () {
    const action: CartActionModel = {
      type: 'removeTaxExemption'
    };
    currentAction.value = action;

    loading.value = true;
    try {
      const response = await $fetch<
        RemoveTaxExemptionMutation['removeTaxExemption']
      >('/api/magento/checkout/taxexemption', {
        method: 'DELETE',
        query: {
          store: $shopStore.value,
          locale: $activeLocale.value.webapp,
          type: activeCartType.value
        }
      });

      if (response?.success) {
        uploadedTaxExemptionFiles.value = [];
      }
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      currentAction.value = null;
      loading.value = false;
    }
  };

  const setActiveCartType = function (cartType: CartType) {
    if (cartType === CartTypes.CART && isAuthenticated.value) {
      cartType = CartTypes.CUSTOMER_CART;
    }

    if (cartType === CartTypes.CUSTOMER_CART && !isAuthenticated.value) {
      cartType = CartTypes.CART;
    }

    activeCartType.value = cartType;
  };

  const onOrderCompleted = async function () {
    const activeCartTypeBefore = activeCartType.value;
    carts.value[activeCartTypeBefore] = null;
    widgetCarts.value[activeCartTypeBefore] = null;
    allItemsSupportFastDelivery.value[activeCartTypeBefore] = null;
    currentPage.value[activeCartTypeBefore] = 1;
    activeCartType.value = CartTypes.REQUEST_CART;
    placedOrder.value = null;
    adyenStateDataId.value[activeCartTypeBefore] = null;
    currentCouponCode.value = null;
    pickupDeliveryId.value = null;
    freightForwarder.value = null;
    agreements.value = [];
    threeDSChallengeError.value = null;

    await loadCart(CartTypes.CUSTOMER_CART);
  };

  const onQuoteCompleted = function () {
    carts.value[CartTypes.REQUEST_CART] = null;
    widgetCarts.value[CartTypes.REQUEST_CART] = null;
    allItemsSupportFastDelivery.value[CartTypes.REQUEST_CART] = null;
    currentPage.value[CartTypes.REQUEST_CART] = 1;
    placedOrder.value = null;
    currentCouponCode.value = null;
  };

  const isCart = computed(() =>
    [CartTypes.CART, CartTypes.CUSTOMER_CART].includes(activeCartType.value)
  );

  const isRequestCart = computed(
    () => activeCartType.value === CartTypes.REQUEST_CART
  );

  return {
    _state,
    currentAction,
    lastAction,
    isInitializing,
    loading,
    itemsPageLoading,
    currentPage,
    activeCurrentPage,
    totalPages,
    totalItemCount,
    pageSize,
    carts,
    widgetCarts,
    relevantCarts,
    activeCartType,
    activeCart,
    currentCartLink,
    cartIds,
    availableCarts,
    shopBaseUrlMap,
    profileLink,
    addProducts,
    addNbqToCart,
    updateProducts,
    removeFromCart,
    loadCart,
    changeCartPage,
    init,
    initialized,
    setActiveCartType,

    /**
     * FOR CHECKOUT
     */
    initForCheckout,
    isCart,
    isMerging,
    isRequestCart,
    isCustomerCartActive,
    numberOfArticles,
    numberOfArticlesCart,
    numberOfArticlesRequestCart,
    numberOfArticlesCustomerCart,
    overAllNumberOfArticlesInRelevantCarts,
    agreements,
    totalExcludingTax,
    total,
    hasTax,
    tax,
    appliedTaxes,
    hasTariff,
    tariff,
    activeCartItems,
    analyticsCartItems,
    shouldShowPricesInRequestCart,
    applyCouponCode,
    currentCouponCode,
    removeCouponCode,
    appliedCouponCodes,
    appliedDiscounts,
    availablePaymentMethods,
    billingIsShippingAddress,
    hasShippingAddress,
    shippingAddress,
    hasBillingAddress,
    billingAddress,
    availableShippingMethods,
    selectedShippingMethod,
    isSelectedShippingMethod,
    isPickupSelected,
    pickupDeliveryId,
    commitPickupDeliveryId,
    freightForwarder,
    commitFreightForwarder,
    addShippingAddress,
    addBillingAddress,
    addDeliveryMethod,
    setPaymentMethod,
    setAdyenStateData,
    addAdyenPaymentDetails,
    adyenStateDataId,
    selectedPaymentMethod,
    hasPayment,
    threeDSChallengeError,
    checkTaxExemption,
    setTaxExemptionChoice,
    removeTaxExemption,
    uploadedTaxExemptionFiles,
    taxExemptionFiles,
    taxExemptSelection,
    taxExemptionMessages,
    uploadTaxExemption,
    placeOrder,
    placedOrder,

    onOrderCompleted,
    onQuoteCompleted
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCartStore, import.meta.hot));
}
